import { useCallback, useMemo } from 'react'
import { useFeatureFlagsStore } from '../store'
import { useTranslate } from '.'

export const useFeatureFlag = (
  flagKey: string | string[],
  {
    returnBoolean = true,
  }: {
    returnBoolean?: boolean
  },
): {
  isFlagEnabled?: boolean
  getBaseTimeRanges?: () => { label: string; value: string }[]
} => {
  // Handle 'allow' special case
  if (flagKey === 'allow') {
    return { isFlagEnabled: true }
  }

  const { t } = useTranslate()

  const { flags } = useFeatureFlagsStore()

  // Helper function to check nested flag value
  const checkNestedFlag = (keys: string[], flagData: any): boolean => {
    let current = flagData
    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key]
      } else {
        return false
      }
    }
    return returnBoolean ? current?.disabled === false : current
  }

  // Check if the flag exists and is enabled
  let isFlagEnabled = false

  if (typeof flagKey === 'string') {
    // Original logic for string input
    isFlagEnabled = flags?.[flagKey]?.value?.disabled === false
  } else if (Array.isArray(flagKey)) {
    // New logic for array input - navigate through nested structure
    isFlagEnabled = checkNestedFlag(flagKey, flags)
  }

  const getBaseTimeRanges = useCallback(
    () =>
      Array.isArray(isFlagEnabled)
        ? [
            ...(isFlagEnabled.includes('1s')
              ? [{ label: t('common_chart_1s'), value: '1s' }]
              : []),
            ...(isFlagEnabled.includes('1m')
              ? [{ label: t('common_chart_1m'), value: '1m' }]
              : []),
            ...(isFlagEnabled.includes('5m')
              ? [{ label: t('common_chart_5m'), value: '5m' }]
              : []),
            ...(isFlagEnabled.includes('10m')
              ? [{ label: t('common_chart_10m'), value: '10m' }]
              : []),
            ...(isFlagEnabled.includes('15m')
              ? [{ label: t('common_chart_15m'), value: '15m' }]
              : []),
            ...(isFlagEnabled.includes('30m')
              ? [{ label: t('common_chart_30m'), value: '30m' }]
              : []),
            ...(isFlagEnabled.includes('1h')
              ? [{ label: t('common_chart_1h'), value: '1h' }]
              : []),
            ...(isFlagEnabled.includes('4h')
              ? [{ label: t('common_chart_4h'), value: '4h' }]
              : []),
            ...(isFlagEnabled.includes('6h')
              ? [{ label: t('common_chart_6h'), value: '6h' }]
              : []),
            ...(isFlagEnabled.includes('8h')
              ? [{ label: t('common_chart_8h'), value: '8h' }]
              : []),
            ...(isFlagEnabled.includes('24h')
              ? [{ label: t('common_chart_24h'), value: '24h' }]
              : []),
            ...(isFlagEnabled.includes('48h')
              ? [{ label: t('common_chart_48h'), value: '48h' }]
              : []),
            ...(isFlagEnabled.includes('72h')
              ? [{ label: t('common_chart_72h'), value: '72h' }]
              : []),
            ...(isFlagEnabled.includes('1d')
              ? [{ label: t('common_chart_1d'), value: '1d' }]
              : []),
            ...(isFlagEnabled.includes('7d')
              ? [{ label: t('common_chart_7d'), value: '7d' }]
              : []),
            ...(isFlagEnabled.includes('14d')
              ? [{ label: t('common_chart_14d'), value: '14d' }]
              : []),
            ...(isFlagEnabled.includes('30d')
              ? [{ label: t('common_chart_30d'), value: '30d' }]
              : []),
            ...(isFlagEnabled.includes('60d')
              ? [{ label: t('common_chart_60d'), value: '60d' }]
              : []),
            ...(isFlagEnabled.includes('90d')
              ? [{ label: t('common_chart_90d'), value: '90d' }]
              : []),
            ...(isFlagEnabled.includes('1M')
              ? [{ label: t('common_chart_1M'), value: '1M' }]
              : []),
            ...(isFlagEnabled.includes('3M')
              ? [{ label: t('common_chart_3M'), value: '3M' }]
              : []),
            ...(isFlagEnabled.includes('6M')
              ? [{ label: t('common_chart_6M'), value: '6M' }]
              : []),
            ...(isFlagEnabled.includes('9M')
              ? [{ label: t('common_chart_9M'), value: '9M' }]
              : []),
            ...(isFlagEnabled.includes('1y')
              ? [{ label: t('common_chart_1y'), value: '1y' }]
              : []),
          ]
        : [],
    [t, isFlagEnabled],
  )

  return { isFlagEnabled, getBaseTimeRanges }
}
